import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../../features/account/context/AuthContext";
import Loading from "../../shared/ui/Loading";

/**
 * ProtectedRoute - Route guard component that protects routes requiring authentication
 * @param {Object} props
 * @param {React.ReactNode} props.children - Component to render if access is granted
 * @param {boolean} props.requireStaff - Whether the route requires staff privileges
 * @param {string} props.redirectTo - Where to redirect unauthorized users (default: "/")
 * @param {React.ReactNode} props.fallback - Custom component to show while checking auth
 */
const ProtectedRoute = ({
    children,
    requireStaff = false,
    redirectTo = "/",
    fallback = <Loading />
}) => {
    const { isAuthenticated, isStaff, loading } = useAuth();
    const location = useLocation();

    // Show loading while authentication is being checked
    if (loading) {
        return fallback;
    }

    // Redirect if not authenticated
    if (!isAuthenticated) {
        return <Navigate to={redirectTo} state={{ from: location }} replace />;
    }

    // Redirect if staff privileges are required but user is not staff
    if (requireStaff && !isStaff) {
        return <Navigate to={redirectTo} state={{ from: location }} replace />;
    }

    // Access granted - render the protected component
    return children;
};

export default ProtectedRoute;
